import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Flame, Mail, CalendarDays, ShieldCheck } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { SYMPTOMS, symptomLabel } from "../data";
import { notify } from "../notify";
import { fetchDiary, addDiaryEntry, deleteDiaryEntry } from "../userdata";

const SEV = ["Very mild", "Mild", "Moderate", "Severe", "Very severe"];
const sevColor = ["#22a35a", "#84c318", "#e08a2b", "#f2683c", "#e0567a"];

export default function SymptomDiary() {
  const [entries, setEntries] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [severity, setSeverity] = useState(3);
  const [symptoms, setSymptoms] = useState([]);
  const [flare, setFlare] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchDiary().then(setEntries).catch(() => {}); }, []);

  const toggle = (id) => setSymptoms((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const add = async () => {
    if (!symptoms.length) { toast.error("Select at least one symptom"); return; }
    setSaving(true);
    try {
      const { entries: fresh } = await addDiaryEntry({ date, severity, symptoms, flare, notes });
      setEntries(fresh);
      setSymptoms([]); setNotes(""); setFlare(false); setSeverity(3);
      notify("diary", `Diary entry ${date}. Severity ${severity}/5${flare ? " (SYMPTOM EPISODE)" : ""}. Symptoms: ${symptoms.map(symptomLabel).join(", ")}. Notes: ${notes || "none"}.`);
      toast.success("Diary entry saved");
      toast.message("Diary entry saved to your account");
    } catch (e) { toast.error("Could not save entry"); }
    setSaving(false);
  };

  const remove = async (id) => {
    try { const fresh = await deleteDiaryEntry(id); setEntries(fresh); } catch (e) { toast.error("Could not delete"); }
  };

  return (
    <AppLayout title="Symptom Diary">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* New entry */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 sticky top-4">
            <h2 className="font-head font-semibold text-slate-800">Log today</h2>
            <div className="mt-4 space-y-4">
              <div>
                <label className="text-[12.5px] font-semibold text-slate-600 mb-1 block">Date</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#0d9488]" />
              </div>
              <div>
                <label className="text-[12.5px] font-semibold text-slate-600 mb-1 block">Overall severity: <span style={{ color: sevColor[severity - 1] }}>{SEV[severity - 1]}</span></label>
                <input type="range" min="1" max="5" value={severity} onChange={(e) => setSeverity(Number(e.target.value))} className="w-full accent-[#0d9488]" />
              </div>
              <div>
                <label className="text-[12.5px] font-semibold text-slate-600 mb-1 block">Symptoms ({symptoms.length})</label>
                <div className="flex flex-wrap gap-1.5 max-h-[200px] overflow-y-auto kl-scroll p-1">
                  {SYMPTOMS.map((s) => (
                    <button key={s.id} onClick={() => toggle(s.id)} className={`px-2.5 py-1 rounded-full text-[12px] font-medium border transition-colors ${symptoms.includes(s.id) ? "bg-[#0d9488] border-[#0d9488] text-white" : "bg-white border-slate-200 text-slate-600 hover:border-[#0d9488]"}`}>{s.label}</button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={flare} onChange={(e) => setFlare(e.target.checked)} className="accent-[#e0567a] w-4 h-4" />
                <span className="text-sm text-slate-600 flex items-center gap-1"><Flame size={15} className="text-[#e0567a]" /> This is a symptom-episode day</span>
              </label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Notes, triggers, medication…" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#0d9488] resize-none" />
              <button onClick={add} disabled={saving} className="w-full h-11 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] disabled:opacity-60 text-white font-semibold text-sm inline-flex items-center justify-center gap-2"><Plus size={17} /> {saving ? "Saving…" : "Save entry"}</button>
            </div>
          </div>
        </div>

        {/* History */}
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-head font-semibold text-slate-800">History</h2>
            <span className="text-[13px] text-slate-400">{entries.length} entries</span>
          </div>
          {entries.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center">
              <CalendarDays size={34} className="text-slate-300 mx-auto" />
              <p className="text-slate-500 mt-3">No entries yet. Log your first symptoms to start tracking patterns.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {entries.map((e) => (
                <div key={e.id} className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-800">{e.date}</span>
                      {e.flare && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#e0567a] bg-[#fdeceb] px-2 py-0.5 rounded-full"><Flame size={11} /> Episode</span>}
                      <span className="text-[12px] font-semibold px-2 py-0.5 rounded-full" style={{ color: sevColor[e.severity - 1], backgroundColor: sevColor[e.severity - 1] + "1a" }}>{SEV[e.severity - 1]}</span>
                    </div>
                    <button onClick={() => remove(e.id)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={17} /></button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {e.symptoms.map((s) => (<span key={s} className="text-[12px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{symptomLabel(s)}</span>))}
                  </div>
                  {e.notes && <p className="text-[13px] text-slate-500 mt-2">{e.notes}</p>}
                </div>
              ))}
            </div>
          )}
          <p className="text-[12px] text-slate-400 flex items-center gap-1.5 mt-4"><ShieldCheck size={13} /> Your diary is stored securely in your ASAL account.</p>
        </div>
      </div>
    </AppLayout>
  );
}
